// @flow
import AttributeModel from "./AttributeModel";
import ConstraintCollection from "./input-constraints/ConstraintCollection";
import RegexConstraint from "./input-constraints/RegexConstraint";
import BSNConstraint from "./input-constraints/BSNConstraint";
import IBANConstraint from "./input-constraints/IBANConstraint";

import { ATTRIBUTE_WIDTH } from "../../constants";

import type { AttributeType, ModelOptions } from "../types";

/**
 * String attribute
 */
export default class StringAttributeModel extends AttributeModel {
  _placeholder: string;

  /**
   */
  constructor(
    attribute: Object,
    attributeContributions: Object,
    modelOptions?: ModelOptions,
  ) {
    super(attribute, attributeContributions, modelOptions);

    this._placeholder = this.getContribution("placeholder", "");
  }

  /**
   * Retrieve initial input value
   */
  getInitialInputValue(value: any): string {
    return this.formatValue(value);
  }

  /**
   */
  static isApplicableModel(contributions: Object): boolean {
    const layouthint = contributions?.layouthint;
    return (
      contributions.type === "string" ||
      (Array.isArray(layouthint) && layouthint.includes("string"))
    );
  }

  /**
   */
  get type(): string {
    return "string";
  }

  /**
   * Get Regexp pattern
   */
  get regexp(): string | null {
    return this.getContribution("regexp");
  }

  /**
   */
  get regexpvalidationmessage(): string | null {
    return this.getContribution("regexpValidationMessage");
  }

  /**
   * Get postfix text
   */
  get postfix(): string {
    return this.getContribution("postfix", "");
  }

  /**
   * Get prefix text
   */
  get prefix(): string {
    return this.getContribution("prefix", "");
  }

  /**
   * Get placeholder text
   */
  get placeholder(): string {
    return this._placeholder;
  }

  /**
   * Set placeholder text
   */
  set placeholder(placeholder: string) {
    this._placeholder = placeholder;
  }

  /**
   */
  isBSN(): boolean {
    return this.layouthint.has("bsn");
  }

  /**
   */
  isIBAN(): boolean {
    return this.layouthint.has("iban");
  }

  /**
   */
  isZipcode(): boolean {
    return this.layouthint.has("zipcode");
  }

  /**
   */
  isEmail(): boolean {
    return this.layouthint.has("email");
  }

  /**
   * Add regex constraints
   */
  addConstraints(): ConstraintCollection {
    const constraints = new ConstraintCollection();

    if (this.isBSN()) {
      constraints.add(new BSNConstraint());
    } else if (this.isIBAN()) {
      constraints.add(new IBANConstraint());
    }

    if (this.isZipcode()) {
      // validate zip when regex is set is set or exact or isNot
      constraints.add(
        new RegexConstraint({
          messageKey: "Constraint.ZipCode.InvalidFormat",
          defaultMessage: "Must be a valid Dutch ZIP code, e.g. 1234 AB",
          regex: /^[1-9]\d{3} ?[a-z]{2}$/gi,
        }),
      );
    } else if (this.isEmail() && this.regexp) {
      constraints.add(
        new RegexConstraint({
          messageKey: "Constraint.Email.InvalidFormat",
          defaultMessage: "Must be a valid e-mail address",
          regex: new RegExp(this.regexp, "gi"),
        }),
      );
    } else if (this.regexp) {
      constraints.add(
        new RegexConstraint({
          messageKey: "Constraint.String.InvalidRegex",
          defaultMessage: this.regexpvalidationmessage,
          regex: this.regexp,
        }),
      );
    }

    return constraints;
  }

  /**
   * Reset attribute to empty string
   */
  reset() {
    this.inputvalue = "";
  }

  /**
   */
  formatIBAN(value: string): string {
    const noFormat = this.removeFormat(value);

    const groups = noFormat.replace(/\s/g, "").match(/.{1,4}/g);
    return groups == null ? "" : groups.join(" ");
  }

  /**
   */
  formatZipcode(value: string): string {
    const noFormat = this.removeFormat(value);
    if (noFormat.length === 6) {
      return (
        noFormat.substring(0, 4) + " " + noFormat.substring(4).toUpperCase()
      );
    }
    return noFormat;
  }

  /**
   */
  formatBSN(value: string): string {
    return this.removeFormat(value);
  }

  /**
   */
  formatValue(value: ?string): string {
    if (value == null || value.toString() === "") {
      return "";
    }

    if (typeof value !== "string") {
      return value;
    }

    if (this.isIBAN()) {
      return this.formatIBAN(value);
    }

    if (this.isZipcode()) {
      return this.formatZipcode(value);
    }

    if (this.isBSN()) {
      return this.formatBSN(value);
    }

    // $FlowIssue
    return value.replaceAll("  ", " \u00A0");
  }

  /**
   */
  removeFormat(value: ?string): string {
    if (value == null || value.toString() === "") {
      return "";
    }

    if (this.isIBAN() || this.isZipcode() || this.isBSN()) {
      // $FlowIssue
      return value.replaceAll(/[^a-z0-9,]/gi, "");
    }

    return value;
  }

  /**
   */
  get validateValue(): string {
    return this.removeFormat(this.inputvalue);
  }

  /**
   */
  get inputvalue(): string {
    return this.getInputValue();
  }

  /**
   * Sets the input value to the value entered by the user
   */
  set inputvalue(value: string) {
    this._inputvalue = value;

    if (value == null) {
      this.value = value;
    } else {
      this.value = this.removeFormat(value);
    }

    this.validate(this.validateValue);
  }

  /**
   */
  get readonlyvalue(): string {
    if (typeof this.value === "string") {
      const formattedValue = this.formatValue(this.value);
      // fix for rendering of multiple spaces
      // $FlowIssue
      return formattedValue.replaceAll("  ", " \u00A0");
    }

    return this.value == null ? "" : this.value;
  }

  /**
   * Update the attribute by name and value
   */
  update(value: string): StringAttributeModel {
    if (this.inputvalue === value) {
      return this;
    }

    this.updateLastModification();
    this.inputvalue = value;

    return this;
  }

  /**
   */
  updateByAttribute(otherAttribute: AttributeType): void {
    this.reset();
    if (otherAttribute instanceof StringAttributeModel) {
      this.update(otherAttribute.value);
    } else {
      console.error(
        `update of string attribute with ${otherAttribute.type} not supported`,
      );
    }
  }

  /**
   */
  get readonlyWidth(): $Keys<typeof ATTRIBUTE_WIDTH> {
    if (this.isZipcode() || this.isBSN()) {
      return ATTRIBUTE_WIDTH.SMALL;
    }

    if (this.isIBAN()) {
      return ATTRIBUTE_WIDTH.LARGE;
    }

    // default value (also applied for email address + download attribute)
    return ATTRIBUTE_WIDTH.MEDIUM;
  }
}
